import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null); // null=checking, false=invalid, obj=valid
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setInvite(false); setError("No invite token provided."); return; }
    api.get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => { setInvite(false); setError(formatApiErrorDetail(e.response?.data?.detail)); });
  }, [token]);

  const submit = async () => {
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/set-password", { token, password });
      await refresh();
      toast.success("Account activated — welcome aboard!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not set password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16" data-testid="set-password-page">
      <div className="w-full max-w-md border border-zinc-900 bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)] p-8">
        <ShieldCheck className="h-8 w-8 text-primary" />
        {invite === null ? (
          <p className="mt-6 font-mono text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Verifying your invite…</p>
        ) : invite === false ? (
          <div className="mt-6" data-testid="invite-invalid">
            <h1 className="font-display font-black text-2xl tracking-tighter">Invite not valid</h1>
            <p className="mt-2 text-sm text-zinc-600">{error || "This link is invalid or has expired."}</p>
            <Button onClick={() => navigate("/login")} className="mt-6 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest">Go to Sign In</Button>
          </div>
        ) : (
          <div className="mt-6">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Welcome, {invite.name?.split(" ")[0] || "there"}</span>
            <h1 className="mt-2 font-display font-black text-3xl tracking-tighter">Set your password</h1>
            <p className="mt-2 text-sm text-zinc-600">Activating <span className="font-medium text-zinc-900">{invite.email}</span> · {invite.role}</p>
            <div className="mt-6 space-y-4">
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Password</Label>
                <Input data-testid="set-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="At least 8 characters" autoComplete="new-password" />
              </div>
              <div>
                <Label className="font-mono text-xs uppercase tracking-widest">Confirm password</Label>
                <Input data-testid="set-password-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 rounded-none border-zinc-300 focus-visible:ring-primary" placeholder="Re-enter password" autoComplete="new-password" onKeyDown={(e) => e.key === "Enter" && submit()} />
              </div>
            </div>
            <Button data-testid="set-password-submit" disabled={submitting} onClick={submit} className="w-full mt-6 rounded-none bg-primary hover:bg-[#5E1F92] text-white font-mono uppercase tracking-widest h-11">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activate Account"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
